import { useState, useRef, useEffect } from 'react';
import { api } from '@/lib/api';
import { MessageSquare, X, Send, Wand2, RotateCcw, ChevronDown } from 'lucide-react';

interface Message { role: 'user' | 'assistant'; content: string }

const STARTERS = [
  'Quelles sont les mentions obligatoires d\'un CDI ?',
  'Comment protéger une marque en France ?',
  'Quelles obligations RGPD pour une PME ?',
  'Clauses indispensables d\'un contrat commercial ?',
];

function Bubble({ msg }: { msg: Message }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
      {!isUser && (
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, flexShrink: 0, marginTop: 2 }}>
          <Wand2 size={14} color="#fff" />
        </div>
      )}
      <div style={{
        maxWidth: '78%', padding: '9px 13px', borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
        background: isUser ? '#B8960A' : '#FAF2DE',
        color: isUser ? '#fff' : '#111827',
        fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
      }}>
        {msg.content}
      </div>
    </div>
  );
}

export default function AiChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [minimized, setMinimized] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    api.get('/ai/status').then(r => setAvailable(r.data.data?.available ?? false)).catch(() => setAvailable(false));
  }, []);

  useEffect(() => {
    if (open && !minimized) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, minimized]);

  useEffect(() => {
    if (open && !minimized) inputRef.current?.focus();
  }, [open, minimized]);

  const send = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput('');
    const newMessages: Message[] = [...messages, { role: 'user', content }];
    setMessages(newMessages);
    setLoading(true);
    try {
      const { data } = await api.post('/ai/chat', {
        messages: newMessages,
        context: 'L\'utilisateur est sur la plateforme VaegAssist. Réponses courtes et pratiques sauf si demande détaillée.',
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠ Erreur de connexion à VaegAI. Vérifiez la clé API Anthropic.' }]);
    } finally { setLoading(false); }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (available === false) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          className="workspace-ai-launcher"
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed', bottom: 24, right: 24, width: 52, height: 52, borderRadius: '50%',
            background: 'linear-gradient(135deg, #7c3aed, #B8960A)',
            border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
            transition: 'transform 0.2s',
          }}
          onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.08)')}
          onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
          title="VaegAI — Assistant juridique"
        >
          <Wand2 size={22} color="#fff" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="workspace-ai-panel" style={{
          position: 'fixed', bottom: 24, right: 24,
          width: 360, height: minimized ? 52 : 520,
          background: '#fff', borderRadius: 16,
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          border: '1px solid #E8D8A8',
          display: 'flex', flexDirection: 'column',
          zIndex: 200, overflow: 'hidden',
          transition: 'height 0.25s ease',
        }}>
          {/* Header */}
          <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg, #7c3aed, #B8960A)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Wand2 size={16} color="#fff" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>VaegAI</div>
              {!minimized && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Assistant juridique · Droit français & OHADA</div>}
            </div>
            <button onClick={() => setMinimized(m => !m)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', padding: 4 }}>
              <ChevronDown size={16} style={{ transform: minimized ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', padding: 4 }}>
              <X size={16} />
            </button>
          </div>

          {!minimized && (
            <>
              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 4px' }}>
                {messages.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 8px' }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>⚖️</div>
                    <p style={{ fontSize: 13, color: '#374151', fontWeight: 500, marginBottom: 4 }}>Bonjour ! Je suis VaegAI.</p>
                    <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16 }}>Posez-moi une question juridique</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {STARTERS.map(s => (
                        <button key={s} onClick={() => send(s)} style={{ padding: '8px 12px', background: '#FEF9EC', border: '1px solid #E8D8A8', borderRadius: 8, fontSize: 12, color: '#374151', cursor: 'pointer', textAlign: 'left', lineHeight: 1.4 }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((m, i) => <Bubble key={i} msg={m} />)}
                    {loading && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Wand2 size={14} color="#fff" />
                        </div>
                        <div style={{ padding: '8px 14px', background: '#FAF2DE', borderRadius: '12px 12px 12px 2px', display: 'flex', gap: 4, alignItems: 'center' }}>
                          {[0,1,2].map(i => (
                            <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af', animation: 'bounce 1s infinite', animationDelay: `${i * 0.2}s` }} />
                          ))}
                          <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}`}</style>
                        </div>
                      </div>
                    )}
                    <div ref={bottomRef} />
                  </>
                )}
              </div>

              {/* Actions bar */}
              {messages.length > 0 && (
                <div style={{ padding: '4px 14px 0', display: 'flex', gap: 8 }}>
                  <button onClick={() => setMessages([])} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                    <RotateCcw size={11} /> Nouvelle conversation
                  </button>
                </div>
              )}

              {/* Input */}
              <div style={{ padding: '10px 12px 12px', borderTop: '1px solid #FAF2DE', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  rows={1}
                  placeholder="Posez votre question juridique..."
                  disabled={loading}
                  style={{
                    flex: 1, padding: '8px 12px', fontSize: 13, border: '1px solid #E8D8A8', borderRadius: 8,
                    resize: 'none', outline: 'none', lineHeight: 1.5, maxHeight: 80, overflowY: 'auto',
                    background: loading ? '#FEF9EC' : '#fff',
                  }}
                  onInput={e => {
                    const el = e.currentTarget;
                    el.style.height = 'auto';
                    el.style.height = Math.min(el.scrollHeight, 80) + 'px';
                  }}
                />
                <button
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  style={{
                    width: 36, height: 36, borderRadius: 8, border: 'none', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                    background: loading || !input.trim() ? '#E8D8A8' : '#7c3aed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    transition: 'background 0.15s',
                  }}
                >
                  <Send size={15} color={loading || !input.trim() ? '#9ca3af' : '#fff'} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
